import { BootResponse, LoginResponse } from '../../constants/apiResponses.types';
import {
  ESessionStorageKeys,
  EPlatformType,
  ELocalStorageKeys,
  EEventsType,
  EResultOptions,
  ELoginResultReason,
  EStorePhase,
  EQueryParams,
  ELoginEvents,
  ELoginMethods,
  ELoginMode,
} from '../../constants/enums';
import useFacebook from '../../hooks/useFacebook';
import { getPlatformData } from '../../utils';
import { localStorageUtil } from 'state/localStorage.state.service';
import useGoogle from '../../hooks/useGoogle';
import useUrlQuery from '../../hooks/useUrlQuery';
import useCustomEvents from '../../hooks/useCustomEvents';
import useLoginEvents from '../../hooks/useLoginEvents';
import { useNavigate } from 'react-router-dom';
import { useObservable } from 'state/observable/observable.state.hook';
import { LoginData, LoginRequestData } from 'pages/login/types';
import useApi from 'hooks/useApi';
import { isMobile, osName, browserName, isIOS, isAndroid } from 'react-device-detect';
import { loginStateService } from 'state/login.state.service';
import { useTranslation } from 'react-i18next';

export const useLoginState = () => {
  const API = useApi({
    platform: isMobile ? osName : browserName,
  });
  const publisherMetaData = API.getPublisherMeta.data as BootResponse;
  const facebook = useFacebook(publisherMetaData.integration.playersAuthentication.fbAppId);
  const google = useGoogle();
  const localAddress = useUrlQuery(EQueryParams.LOCAL_ADDRESS);
  const utmSource = useUrlQuery(EQueryParams.UTM_SOURCE);
  const customEvents = useCustomEvents();
  const { sendPublisherWebhookLoginEvent } = useLoginEvents();
  const navigate = useNavigate();
  const isCampaignPath = location.pathname.includes('/campaign/');
  const { t } = useTranslation();

  const loginResponse = async (response: LoginData) => {
    loginStateService.setProcessing(true);
    if (response.authMethod === 'facebook') {
      try {
        response.profileImageUrl = await facebook.getFacebookProfile(response.userToken!);
      } catch (error) {
        console.error('Fail to load profile picture', error);
        // Handle the error or take appropriate action here
      }
    }
    if (response.authMethod === 'google') {
      response.profileImageUrl = await google.getGoogleProfile(response.userToken!);
    }
    const sessionData = localStorageUtil.get(ESessionStorageKeys.SESSION_DATA);
    const loginData: {
      data: LoginRequestData;
    } = {
      data: {
        publisherId: publisherMetaData.storeTheme.publisherId,
        localAddress: localAddress,
        sessionId: sessionData.id,
        ...response,
      },
    };
    if (publisherMetaData?.featureFlags.store_send_os_authlogin) {
      loginData.data.os = isIOS
        ? EPlatformType.IOS
        : isAndroid
          ? EPlatformType.ANDROID
          : EPlatformType.WEB;
    }
    if (response.authMethod === 'otp' && loginStateService.proofKey.get()) {
      loginStateService.setLoggingWithProofKey(true);
    }

    if (!isCampaignPath) {
      try {
        const data: { data: LoginResponse } = await API.login.mutateAsync(loginData.data);
        const loginDataToSend = {
          result: EResultOptions.SUCCESS,
          playerId: data.data.playerId,
        };
        if (data.data.errorCode === 100) {
          return failedLoginHandler(response.authMethod, data.data.errorMessage);
        }
        data.data.sessionToken &&
          localStorageUtil.set(ELocalStorageKeys.SESSION_TOKEN, data.data.sessionToken);
        localStorageUtil.set(ELocalStorageKeys.PLAYER_DATA, {
          playerId: data.data.playerId,
          playerName: data.data.playerName,
          playerCountry: data.data.playerCountry,
          profileImageUrl: data.data.profileImageUrl,
        });
        customEvents.setDistinctId();
        customEvents.sendCustomEvent(
          EEventsType.LOGIN_RESULT,
          {
            authMethod: response.authMethod,
            ...loginDataToSend,
            platform: getPlatformData(),
          },
          EStorePhase.POST_LOGIN
        );
        sendPublisherWebhookLoginEvent(
          ELoginEvents.LOGIN_RESOLVED,
          response.authMethod,
          loginDataToSend
        );
        loginStateService.setProcessing(false);
        navigate(`/shop${utmSource ? `?utm_source=${utmSource}` : ''}&login_redirect=true`);
      } catch (error: any) {
        loginStateService.setProcessing(false);
        if (error.response?.data.code === 100) {
          failedLoginHandler(response.authMethod, error.response?.data.message);
        } else if (error.response?.data.code >= 500 && error.response?.data.code <= 3000) {
          failedLoginHandler(response.authMethod, t('login.errorMessages.serviceIsDown'));
        } else {
          failedLoginHandler(response.authMethod, t('login.errorMessages.loginFailed'));
        }
      }
    }
  };

  const failedLoginHandler = async (method: string, msg?: string) => {
    const loginFailureData = {
      result: EResultOptions.FAILED,
      reason: msg ?? ELoginResultReason.UNKNOWN,
    };
    customEvents.sendCustomEvent(
      EEventsType.LOGIN_RESULT,
      {
        authMethod: method,
        ...loginFailureData,
        platform: getPlatformData(),
      },
      EStorePhase.PRE_LOGIN
    );
    sendPublisherWebhookLoginEvent(
      ELoginEvents.LOGIN_RESOLVED,
      method as ELoginMethods,
      loginFailureData
    );
    navigate(`/failed?error=auth${msg ? `&msg=${msg}` : ''}`);
  };

  const setMode = (mode: ELoginMode) => {
    return mode !== 'providers' ? navigate(`./${mode}`) : navigate(-1);
  };

  const openInfoModal = (url?: string) => {
    loginStateService.setIframeUrl(url || '');
    loginStateService.setShowInfoModal(true);
  };

  const closeInfoModal = () => {
    loginStateService.setShowInfoModal(false);
  };

  return {
    processing: useObservable(loginStateService.processing),
    proofKey: useObservable(loginStateService.proofKey),
    loggingWithProofKey: useObservable(loginStateService.loggingWithProofKey),
    showInfoModal: useObservable(loginStateService.showInfoModal),
    iframeUrl: useObservable(loginStateService.iframeUrl),
    setProcessing: loginStateService.setProcessing,
    setProofKey: loginStateService.setProofKey,
    setLoggingWithProofKey: loginStateService.setLoggingWithProofKey,
    loginResponse,
    failedLoginHandler,
    setMode,
    openInfoModal,
    closeInfoModal,
    clear: loginStateService.clear,
  };
};
